import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Skeleton from "@mui/material/Skeleton";
import { useQueryHandler } from "@sinch/hooks/useQueryHandler";
import React, { FC, Suspense, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { DialogCloseIcon } from "../../DialogCloseIcon/DialogCloseIcon";
import { PendingChangesReview } from "./PendingChanges/PendingChangesReview";
import { WorkerReviewInfo } from "./queries.graphql";
import type { WorkerReviewInfoQuery, WorkerReviewInfoQueryVariables } from "./queries.types";
import { isNotNil } from "ramda-adjunct";
import Stack from "@mui/material/Stack";
import { MdsIcon } from "@sinch/components";
import { MdsOpenInNewXs } from "@sinch/icons";
import { ReviewProfileDialogContent } from "./MandatoryAttributes/ReviewProfileDialogContent";
import { ModalProps } from "@mui/material/Modal";
import { TableLoadingSkeleton } from "./TableLoadingSkeleton";
import { StaffHeader } from "@sinch/components/application/Staff/StaffHeader/StaffHeader";
import { Box, Paper } from "@mui/material";

export type ProfileReviewMode = "initial_review" | "pending_changes";

interface ProfileReviewDialogProps {
  mode: ProfileReviewMode;
  onClose: () => void;
  onSuccess?: () => void;
  workerId: string;
}

export const ProfileReviewDialog: FC<ProfileReviewDialogProps> = ({ mode, onClose, onSuccess, workerId }) => {
  const { t } = useTranslation();
  // Mode is internal state so we can auto-advance from initial_review to pending_changes
  // after a successful profile approval when the worker has pending attribute changes
  // queued from signup.
  const [currentMode, setCurrentMode] = useState<ProfileReviewMode>(mode);

  const {
    data: workerData,
    loading: workerLoading,
    refetch: refetchWorkerInfo,
  } = useQueryHandler<WorkerReviewInfoQuery, WorkerReviewInfoQueryVariables>(WorkerReviewInfo, { workerId });

  const handleApproved = useCallback(async () => {
    // Refetch worker info to get fresh hasPendingChanges — the cached value from
    // the initial query load may be stale after the approve mutation.
    const { data: freshWorkerData } = await refetchWorkerInfo();
    // If the worker has pending attribute changes (queued during signup before they were
    // approved), advance the dialog to the pending changes review step instead of closing.
    if (freshWorkerData?.worker?.workerinfo?.hasPendingChanges === true) {
      setCurrentMode("pending_changes");
      return;
    }
    (onSuccess ?? onClose)();
  }, [refetchWorkerInfo, onSuccess, onClose]);

  const handleDeclined = useCallback(() => {
    (onSuccess ?? onClose)();
  }, [onSuccess, onClose]);

  const handleClose: ModalProps["onClose"] = useCallback(
    (_, reason) => {
      if (reason !== "escapeKeyDown") {
        onClose();
      }
    },
    [onClose]
  );

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      onClose={handleClose}
      open
      slotProps={{
        paper: {
          "data-cy": "profile-review-dialog",
          "data-mode": currentMode,
        } as React.HTMLAttributes<HTMLDivElement>,
      }}
    >
      <DialogTitle>
        {currentMode === "pending_changes" ? t("ProfileReview.reviewPendingChanges") : t("ProfileReview.reviewProfile")}
        <DialogCloseIcon onClick={onClose} />
      </DialogTitle>
      <DialogContent sx={{ pb: 0 }}>
        <Stack alignItems="flex-start" gap={1}>
          {workerLoading ? (
            <Skeleton height={64} variant="rectangular" />
          ) : (
            isNotNil(workerData?.worker) && (
              <>
                <Paper sx={{ p: 2 }} variant="outlined">
                  <StaffHeader avatarSize="lg" staff={workerData!.worker} />
                  <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      href={decodeURI(`/admin/workers/view/${workerData!.worker.id}`)}
                      size="small"
                      startIcon={<MdsIcon icon={MdsOpenInNewXs} />}
                      target="_blank"
                      variant="outlined"
                    >
                      {t("Action.view")}
                    </Button>
                  </Box>
                </Paper>
              </>
            )
          )}
        </Stack>
      </DialogContent>
      {currentMode === "pending_changes" && (
        <Suspense fallback={<TableLoadingSkeleton />}>
          <PendingChangesReview onClose={onClose} onSuccess={onSuccess} workerId={workerId} />
        </Suspense>
      )}
      {currentMode !== "pending_changes" && (
        <Suspense fallback={<TableLoadingSkeleton />}>
          <ReviewProfileDialogContent
            onApproved={handleApproved}
            onClose={onClose}
            onDeclined={handleDeclined}
            workerId={workerId}
          />
        </Suspense>
      )}
    </Dialog>
  );
};
